import { AbstractControl, FormArray, Validators } from '@angular/forms';
import { Caregiver } from '@models/patient';
import { TrainTravelType } from '@models/request';
import { PersistentFormControl, PersistentFormGroup } from '@utility/persistent-forms';
import { FormGroupValueOf } from '@utility/utility';
import {
    disallowedValueValidator,
    equalOrMoreThanTodayOnlyIfCRCDateValidator,
    maxLengthValidator,
    notWhitespaceValidator,
} from '@utility/utility.validators';
import { Moment } from 'moment';

export class SingleTrainTripFormGroup extends PersistentFormGroup<{
    travelType: PersistentFormControl<TrainTravelType | null>;
    stationOrigin: PersistentFormControl<string | null>;
    stationDestination: PersistentFormControl<string | null>;
    departureDate: PersistentFormControl<Moment | null>;
    returnDate: PersistentFormControl<Moment | null>;
    specialRequests: PersistentFormControl<string | null>;
}> {
    public static create(value: FormGroupValueOf<SingleTrainTripFormGroup>, noTravelAllowed: boolean) {
        const formGroup = new SingleTrainTripFormGroup(
            {
                travelType: new PersistentFormControl(value.travelType, [
                    disallowedValueValidator(TrainTravelType.NoTravel, noTravelAllowed, 'Value is required'),
                ]),
                stationOrigin: new PersistentFormControl(value.stationOrigin, [
                    Validators.required,
                    notWhitespaceValidator(),
                    maxLengthValidator(70),
                ]),
                stationDestination: new PersistentFormControl(value.stationDestination, [
                    Validators.required,
                    notWhitespaceValidator(),
                    maxLengthValidator(70),
                ]),
                departureDate: new PersistentFormControl(value.departureDate, [
                    Validators.required,
                    equalOrMoreThanTodayOnlyIfCRCDateValidator,
                ]),
                returnDate: new PersistentFormControl(value.returnDate, [
                    Validators.required,
                    equalOrMoreThanTodayOnlyIfCRCDateValidator,
                ]),
                specialRequests: new PersistentFormControl(value.specialRequests, [
                    notWhitespaceValidator(),
                    maxLengthValidator(1000),
                ]),
            },
            [
                (control: AbstractControl<SingleTrainTripFormGroup['value']>) => {
                    const value = control.value;
                    if (!value.departureDate || !value.returnDate) return null;
                    return value.departureDate <= value.returnDate
                        ? null
                        : {
                              invalidDates: 'Return date cannot be before departure date.',
                          };
                },
            ]
        );
        return formGroup;
    }
}

export class CaregiverTrainTripFormGroup extends PersistentFormGroup<{
    details: PersistentFormGroup<{
        bookIdenticalTrip: PersistentFormControl<boolean | null>;
        trainDetails: SingleTrainTripFormGroup;
    }>;
    caregiver: PersistentFormControl<Caregiver>; // helper property, not visible in UI
}> {
    public static create(value: FormGroupValueOf<CaregiverTrainTripFormGroup>) {
        return new CaregiverTrainTripFormGroup({
            caregiver: new PersistentFormControl(value.caregiver),
            details: new PersistentFormGroup({
                trainDetails: SingleTrainTripFormGroup.create(value.details.trainDetails, true),
                bookIdenticalTrip: new PersistentFormControl(value.details.bookIdenticalTrip),
            }),
        });
    }
}

export class TrainRequestDetailsFormGroup extends PersistentFormGroup<{
    patientTrainTripDetails: SingleTrainTripFormGroup;
    caregivers: FormArray<CaregiverTrainTripFormGroup>;
}> {
    public static create(value: FormGroupValueOf<TrainRequestDetailsFormGroup>): TrainRequestDetailsFormGroup {
        return new TrainRequestDetailsFormGroup({
            patientTrainTripDetails: SingleTrainTripFormGroup.create(value.patientTrainTripDetails, false),
            caregivers: new FormArray(
                value.caregivers.map((caregiverDetails) => CaregiverTrainTripFormGroup.create(caregiverDetails))
            ),
        });
    }
}
